import React, { useState, useEffect, useRef } from 'react';
import { DataTable, DropdownMenu, Text, CardSurface, Spinner, Search, TruncatedText } from '@spark';
import OverviewPieChart from './OverviewPieChart';
import { buildOverviewPieData } from '@/shared/utils/pieChartUtils';
import EntityModal from './EntityModal';
import { buildModelStatusColumn } from './EntityStatusColumns';
import { getTextDirectionStyle } from '@/shared/utils/textUtils';
import { ModelResponsesExportButton } from './ModelResponsesExportButton';
import { useAuth } from '@/features/auth/AuthContext';
import {
    StatusType,
    calculateSampleMetrics,
    calculateRelationMetrics,
} from './entityStatusMetrics';


const OVERVIEW_CARD_MIN_HEIGHT = 280;

const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// Re-exported for backwards compatibility — ``EntityModal`` and
// ``TaggedSample`` import ``StatusType`` from here. Source of truth is
// ``entityStatusMetrics`` so the Playground and Evaluation share one enum.
export { StatusType };

/**
 * Render mode for EntityExtractionsTable.
 *  - 'all'      (default): renders the original layout (header + overview pies + samples table)
 *  - 'overview': renders only the two pie charts (used by Evaluation > Overview tab for NER)
 *  - 'samples':  renders only the search/export bar + sample comparison table (used by Evaluation > Samples tab for NER)
 */
export const EE_RENDER_MODES = {
    ALL: 'all',
    OVERVIEW: 'overview',
    SAMPLES: 'samples',
};

const EntityExtractionsTable = ({
    displayNames,
    llmUsecaseId,
    version,
    currentUsecase,
    teacherModelProcessing = false,
    teacherModelFailed = false,
    teacherModelErrorDetails = null,
    studentModelProcessing = false,
    compareWith = 'oldModel',
    comparisonModelName,
    hasBenchmark = false,
    evaluationDatasets = [],
    evaluationBenchmarks = [],
    selectedLabel = "All Labels",
    hideHeader = false,
    data,
    sampleCounters,
    pagination,
    onTableChange,
    setFilters,
    setPagination,
    filters,
    isVersionComparison,
    allEntities,
    activeTab,
    loading,
    comparisonVersion,
    mode = EE_RENDER_MODES.ALL,
    selectedDistillationEventId = null,
}) => {
    const showOverview = mode === EE_RENDER_MODES.ALL || mode === EE_RENDER_MODES.OVERVIEW;
    const showSamples = mode === EE_RENDER_MODES.ALL || mode === EE_RENDER_MODES.SAMPLES;
    const showHeader = !hideHeader && mode === EE_RENDER_MODES.ALL;
    const { role } = useAuth();
    // Derive isFileComparison from compareWith prop
    const isFileComparison = compareWith === 'file';
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedSample, setSelectedSample] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [extractionTypeCounts, setExtractionTypeCounts] = useState({
        newModel: {
            all: 0,
            bad: 0,
            good: 0
        },
        comparisonModel: {
            all: 0,
            bad: 0,
            good: 0
        }
    });

    const handleExtractionTypeChange = (value, filterKey) => {
        const filterValue = value === 'all' ? null : value;

        if (setFilters && filterKey) {
            setFilters(prev => ({
                ...prev,
                [filterKey]: filterValue,
            }));
        }

        // Reset pagination when changing extraction type filters so that we
        // don't stay on a page index that no longer exists for the new filter
        // (e.g. going from page 18 of "Partial" to "Successful" which only
        // has 8 pages would otherwise result in an empty "No data" state).
        if (setPagination) {
            setPagination(prev => ({
                ...prev,
                current: 1,
            }));
        }
    };

    // Handle sample text filter change
    const handleSampleFilterChange = (e) => {
        const value = e.target.value;
        if (setFilters) {
            setFilters(prev => ({
                ...prev,
                example: value,
            }));
        }
        // Reset to first page when changing filters
        if (setPagination) {
            setPagination(prev => ({
                ...prev,
                current: 1,
            }));
        }
    };

    // Reusable extraction type select component
    const renderExtractionTypeSelect = (filterKey) => {
        const value = filters[filterKey] || "all";
        const items = [
            { id: 'all', label: 'All responses' },
            { id: 'success', label: 'Successful extractions' },
            ...(activeTab === 'entities' ? [{ id: 'partial', label: 'Partial extractions' }] : []),
            { id: 'failed', label: 'Failed extractions' },
        ];
        const selectedText = items.find(item => item.id === value)?.label || 'All responses';

        return (
            <div className='fs-unmask'>
                <DropdownMenu
                    items={items}
                    text={selectedText}
                    onSelect={(item) => handleExtractionTypeChange(item.id, filterKey)}
                    size="small"
                    showLabel={false}
                />
            </div>
        );
    };

    const renderPieChart = (isComparisonModel = false) => {
        const modelName = isComparisonModel ? comparisonModelName : `SpecificAI V${version}`;
        const titleParts = [modelName, "overview"];

        const key = isComparisonModel ? 'oldModel' : 'newModel';
        const counts = {
            successful: (sampleCounters?.correct?.[key]) ?? 0,
            miscategorized: (sampleCounters?.miscategorized?.[key] ?? 0),
            misaligned: (sampleCounters?.misaligned?.[key] ?? 0),
            misalignedMiscategorized: (sampleCounters?.misaligned_miscategorized?.[key] ?? 0),
            missed: (sampleCounters?.missed?.[key] ?? 0),
            fabricated: (sampleCounters?.fabricated?.[key] ?? 0),
            outOfContent: (sampleCounters?.out_of_content?.[key] ?? 0),
        };

        const isRelationsTab = activeTab === 'relations';
        const pieData = buildOverviewPieData({
            isRelationsTab,
            counts,
        });

        return (
            <OverviewPieChart
                titleMain={titleParts[0]}
                titleSub={titleParts[1]}
                data={pieData}
                width={185}
                height={185}
                outerRadius={74}
                fullWidth={!comparisonModelName}
            />
        );
    };

    // Grouped DataTable column for one model (name + filter header over
    // per-status sub-columns). Metrics derivation mirrors the bar-chart era.
    const buildModelColumn = ({ key, modelName, filterKey, isProcessing, isNewModel }) => {
        const isRelations = activeTab === 'relations';
        return buildModelStatusColumn({
            key,
            title: (
                <>
                    <TruncatedText text={modelName} maxWidth={240} className="fs-unmask" />
                    {renderExtractionTypeSelect(filterKey)}
                </>
            ),
            isRelations,
            allEntities,
            isProcessing,
            isEmptyResponse: (record) =>
                isNewModel
                    ? !!record.new_model_response_empty_in_db
                    : isVersionComparison
                        ? !!record.comparison_model_response_empty_in_db
                        : !!record.teacher_response_empty_in_db,
            getMetrics: (record) =>
                isRelations
                    ? calculateRelationMetrics(record, isNewModel)
                    : calculateSampleMetrics(
                        isNewModel
                            ? record.new_model_entities_status
                            : record.teacher_model_entities_status ||
                                  record.comparison_model_entities_status
                    ),
        });
    };

    // For debugging purposes only, log totals when changing pages
    const lastLogRef = useRef({ signature: '', newTotals: '', comparisonTotals: '' });
    useEffect(() => {
        try {
            if (!Array.isArray(data) || !pagination || !pagination.current) return;
            const currentPage = pagination.current;
            // With server-side pagination, `data` already contains only the current page records
            const pageRecords = data;
            const signature = `${currentPage}:${activeTab}`;

            const computeTotals = (isNewModel) => {
                let totals = { correct: 0, fabricated: 0, missed: 0, miscategorized: 0 };
                pageRecords.forEach(sample => {
                    if (activeTab === 'relations') {
                        const metrics = calculateRelationMetrics(sample, isNewModel);
                        totals.correct += metrics?.successful || 0;
                        const fb = metrics?.countItemsByStatus?.failedBreakdown || {};
                        totals.fabricated += fb.fabricated || 0;
                        totals.missed += fb.missed || 0;
                        totals.miscategorized += fb.miscategorized || 0;
                    } else {
                        const statuses = isNewModel ? sample?.new_model_entities_status : (sample?.teacher_model_entities_status || sample?.comparison_model_entities_status);
                        const metrics = calculateSampleMetrics(statuses);
                        totals.correct += metrics?.successful || 0;
                        // No detailed breakdown for entities view
                    }
                });
                return totals;
            };

            const totalsNew = computeTotals(true);
            const totalsNewStr = `${totalsNew.correct}|${totalsNew.fabricated}|${totalsNew.missed}|${totalsNew.miscategorized}`;
            let totalsComparisonStr = '';
            let totalsComparison;
            if (comparisonModelName) {
                totalsComparison = computeTotals(false);
                totalsComparisonStr = `${totalsComparison.correct}|${totalsComparison.fabricated}|${totalsComparison.missed}|${totalsComparison.miscategorized}`;
            }

            // Skip if nothing changed for this signature
            if (
                lastLogRef.current.signature === signature &&
                lastLogRef.current.newTotals === totalsNewStr &&
                lastLogRef.current.comparisonTotals === totalsComparisonStr
            ) {
                return;
            }

            // Update ref then log
            lastLogRef.current = { signature, newTotals: totalsNewStr, comparisonTotals: totalsComparisonStr };
            console.log(`[Page Change] page=${currentPage} tab=${activeTab} model=SpecificAI V${version} | correct=${totalsNew.correct}, fabricated=${totalsNew.fabricated}, missed=${totalsNew.missed}, miscategorized=${totalsNew.miscategorized}`);
            if (comparisonModelName && totalsComparison) {
                console.log(`[Page Change] page=${currentPage} tab=${activeTab} model=${comparisonModelName} | correct=${totalsComparison.correct}, fabricated=${totalsComparison.fabricated}, missed=${totalsComparison.missed}, miscategorized=${totalsComparison.miscategorized}`);
            }
        } catch (e) {
            console.warn('Failed to compute page totals', e);
        }
    }, [data, pagination?.current, pagination?.pageSize, activeTab, comparisonModelName, version]);

    return (
        <div style={{ marginTop: 5, marginBottom: 20 }}>
            {showHeader && (
                <div style={{ marginBottom: 25 }} className="fs-unmask">
                    <Text variant="h3-bold">
                        {activeTab === 'relations' ? 'Relation Extractions' : 'Entity Extractions'}
                    </Text>
                    <Text variant="base-regular" color="secondary" style={{ display: 'block', marginTop: 4 }}>
                        Review and analyze {activeTab === 'relations' ? 'relations' : 'entities'} extracted by the SpecificAI model and comparison models.
                    </Text>
                </div>
            )}

            {/* Overview Section */}
            {showOverview && (
                <div className="fs-unmask" style={{
                    display: 'flex',
                    gap: 24,
                    marginBottom: 32,
                    borderRadius: '12px'
                }}>
                    {comparisonModelName && <div style={{ flex: 1 }}>
                        {teacherModelProcessing && (
                            <CardSurface style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: OVERVIEW_CARD_MIN_HEIGHT,
                                textAlign: 'center'
                            }}>
                                <div className='fs-unmask' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Spinner size="small" />
                                    <Text variant="base-regular">Processing...</Text>
                                </div>
                            </CardSurface>
                        )}
                        {teacherModelFailed && !teacherModelProcessing && (
                            <CardSurface style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: OVERVIEW_CARD_MIN_HEIGHT,
                                textAlign: 'center'
                            }}>
                                <div className='fs-unmask'>
                                    <Text variant="base-regular" color="error">
                                        Teacher evaluation failed
                                    </Text>
                                </div>
                            </CardSurface>
                        )}
                        {!teacherModelProcessing && !teacherModelFailed && renderPieChart(true)}
                    </div>}
                    <div style={{ flex: 1 }}>
                        {studentModelProcessing && (
                            <CardSurface style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: OVERVIEW_CARD_MIN_HEIGHT,
                                textAlign: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Spinner size="small" />
                                    <Text variant="base-regular">Processing...</Text>
                                </div>
                            </CardSurface>
                        )}
                        {!studentModelProcessing && renderPieChart(false)}
                    </div>
                </div>
            )}

            {showSamples && (<>
                {/* Search and Export Section */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    padding: '16px 0',
                    borderBottom: '1px solid #f0f0f0',
                }}>
                    <Search
                        size="medium"
                        placeholder="Search samples"
                        value={filters?.example || ''}
                        onChange={handleSampleFilterChange}
                        showClearButton={!!(filters?.example)}
                        onClear={() => handleSampleFilterChange({ target: { value: '' } })}
                        style={{ width: 463 }}
                    />
                    <ModelResponsesExportButton
                        currentUsecase={currentUsecase}
                        version={version}
                        llmUsecaseId={llmUsecaseId}
                        filters={filters}
                        isVersionComparison={isVersionComparison}
                        comparisonVersion={comparisonVersion}
                        comparisonModelName={comparisonModelName}
                        disabled={loading}
                        role={role}
                        isFileComparison={isFileComparison}
                        activeTab={activeTab}
                        selectedDistillationEventId={selectedDistillationEventId}
                    />
                </div>

                {/* Sample Comparisons Section */}
                <DataTable
                    dataSource={data}
                    rowKey={(record) => (`row-${record.id}`)}
                    rowHeight={90}
                    pagination={pagination ? {
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page) => onTableChange({ current: page, pageSize: pagination.pageSize }),
                    } : null}
                    showHeader={true}
                    loading={loading}
                    columns={[
                        {
                            title: <span className="fs-unmask" style={{ whiteSpace: 'nowrap' }}>Samples</span>,
                            dataIndex: 'sample',
                            key: 'sample',
                            width: 314,
                            render: (text, record) => {
                                const truncated = truncateText(record.request_example || '', 420);
                                const directionStyle = getTextDirectionStyle(truncated);
                                return (
                                    <Text
                                        variant="base-regular"
                                        color="primary"
                                        as="p"
                                        style={{
                                            margin: 0,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            ...directionStyle,
                                        }}
                                        onClick={() => {
                                            setSelectedSample(record);
                                            setModalVisible(true);
                                        }}
                                    >
                                        {truncated}
                                    </Text>
                                );
                            },
                        },
                        ...(comparisonModelName ? [buildModelColumn({
                            key: 'comparisonModel',
                            modelName: comparisonModelName,
                            filterKey: activeTab === 'relations' ? 'oldModelRelationStatus' : 'oldModelStatus',
                            isProcessing: teacherModelProcessing,
                            isNewModel: false,
                        })] : []),
                        buildModelColumn({
                            key: 'specificai',
                            modelName: `SpecificAI V${version}`,
                            filterKey: activeTab === 'relations' ? 'newModelRelationStatus' : 'newModelStatus',
                            isProcessing: studentModelProcessing,
                            isNewModel: true,
                        }),
                    ]}
                />
                <EntityModal
                    key={selectedSample?.request_example}
                    visible={modalVisible}
                    onClose={() => setModalVisible(false)}
                    sample={selectedSample}
                    comparisonModelName={comparisonModelName}
                    version={version}
                    teacherModelProcessing={teacherModelProcessing}
                    studentModelProcessing={studentModelProcessing}
                    allEntities={allEntities}
                    activeTab={activeTab}
                />
            </>)}
        </div >
    );
};

/**
 * Renders only the overview pies (top portion of EntityExtractionsTable).
 * Used by the new Evaluation > Overview tab for NER task type.
 */
export const EntityExtractionsOverview = (props) => (
    <EntityExtractionsTable {...props} mode={EE_RENDER_MODES.OVERVIEW} />
);

/**
 * Renders only the search/export bar + sample comparison table.
 * Used by the new Evaluation > Samples tab for NER task type.
 */
export const EntityExtractionsSamples = (props) => (
    <EntityExtractionsTable {...props} mode={EE_RENDER_MODES.SAMPLES} />
);


export default EntityExtractionsTable;
